/** Pezzi condivisi della Gestione Tornei (Control Center e app staff). */
import { useCallback, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Minus, Plus, X } from "lucide-react";
import { Toast, type ToastKind } from "@/components/Toast";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/utils";
import { gare, STATUS_LABEL, type GaraStatus } from "@/lib/gare";

/** "/staff" nell'app staff, "/gare" nel Control Center. */
export function useBase(): "/staff" | "/gare" {
  return useLocation().pathname.startsWith("/staff") ? "/staff" : "/gare";
}

export const garaPath = (base: string, id: number) => (base === "/staff" ? `/staff/gara/${id}` : `/gare/${id}`);

export function useActor() {
  return useQuery({ queryKey: ["gare", "me"], queryFn: gare.me, staleTime: 300_000 });
}

export function useFlash(): [ReactNode, (kind: ToastKind, msg: string) => void] {
  const [t, setT] = useState<{ kind: ToastKind; msg: string; k: number } | null>(null);
  const close = useCallback(() => setT(null), []);
  const show = useCallback((kind: ToastKind, msg: string) => setT({ kind, msg, k: Date.now() }), []);
  const node = t ? <Toast key={t.k} kind={t.kind} message={t.msg} onClose={close} autoHideMs={t.kind === "error" ? 6000 : 2500} /> : null;
  return [node, show];
}

export function StatusBadge({ status }: { status: GaraStatus }) {
  const v = status === "live" ? "success" : status === "finished" ? "neutral" : "warning";
  return <Badge variant={v} dot={status === "live"}>{STATUS_LABEL[status]}</Badge>;
}

/** Contatore grande da usare col pollice: − valore + */
export function Stepper({ value, onChange, max = 30, label }: {
  value: number; onChange: (v: number) => void; max?: number; label: string;
}) {
  return (
    <div className="flex items-center gap-1.5" role="group" aria-label={label}>
      <button type="button" aria-label={`Togli un game a ${label}`} onClick={() => onChange(Math.max(0, value - 1))}
        className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-700 active:bg-slate-100">
        <Minus className="h-5 w-5" />
      </button>
      <input inputMode="numeric" pattern="[0-9]*" aria-label={`Game di ${label}`} value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value.replace(/\D/g, "") || "0", 10);
          onChange(Math.min(max, n));
        }}
        className="h-11 w-12 rounded-lg border border-slate-300 text-center text-xl font-bold tabular-nums text-slate-900 focus:border-moving-500 focus:outline-none focus:ring-2 focus:ring-moving-500" />
      <button type="button" aria-label={`Aggiungi un game a ${label}`} onClick={() => onChange(Math.min(max, value + 1))}
        className="flex h-11 w-11 items-center justify-center rounded-lg bg-moving-500 text-white active:bg-moving-700">
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title}
        className="w-full max-w-md rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Chiudi" className="rounded-md p-1 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Section({ title, right, children, className }: {
  title: ReactNode; right?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={cn("rounded-xl border border-slate-200 bg-white shadow-sm", className)}>
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export const lvl = (v: number | null | undefined) => (v === null || v === undefined ? "" : v.toFixed(1).replace(".", ","));
